import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, Popconfirm, Skeleton, Table } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';

import { useAppSelector } from 'app/store';
import {
  useApproveAbsenceMutation,
  useGetSingleAbsenceQuery,
  useRejectAbsenceMutation,
} from 'app/services/edpApiV1/absence';

import { uiModel } from 'entities/ui';

import {
  dataObjectForAbsencesTable,
  dataObjectForFormAbsences,
  getErrorData,
  getZupStatusName,
  sortingDate,
} from 'shared/helpers/utils';
import { useNotifications } from 'shared/hooks/use-notifications';
import { useIndicators } from 'features/indicators';
import { TPreparedSingleAbsenceData, TPreparedSingleAbsenceTable } from 'shared/types/absence.types';
import AbsenceComment from '../AbsenceComment';
import AbsenceUserForm from '../absenceUserForm';
import style from './index.module.scss';
/**
 * @page Absence user
 */

const columns: ColumnsType<TPreparedSingleAbsenceTable> = [
  {
    title: '№',
    dataIndex: 'id',
    key: 'key',
    width: 120,
  },
  {
    title: 'Дата',
    dataIndex: 'created',
    key: 'created',
    sorter: (a, b) => sortingDate(a.created, b.created),
  },
  {
    title: 'Период',
    dataIndex: 'period',
    key: 'period',
  },
  {
    title: 'ФИО',
    dataIndex: 'fio',
    key: 'fio',
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    key: 'status',
    render: (status, render) => {
      return (
        <>
          <div>
            <Badge status={status.type} />
            {status.name}
          </div>

          {render?.zupRequestStatus?.type && (
            <div>
              <Badge className={style.badge} status={render?.zupRequestStatus?.type} />
              {getZupStatusName(render?.zupRequestStatus?.type)}
            </div>
          )}
        </>
      );
    },
  },
];

const AbsenceUser = () => {
  const [dataAbsence, setDataAbsence] = useState<TPreparedSingleAbsenceData[] | []>([]);
  const dataForTable = useRef<TPreparedSingleAbsenceTable[]>();

  const { id } = useParams();

  const { blockHrActions } = useAppSelector(uiModel.Common.selectors.selectCommon);

  const {
    data: dataSingleAbsence,
    isLoading,
    isSuccess: isSuccessGetSingleAbsence,
    isFetching: isFetchingGetSingleAbsence,
  } = useGetSingleAbsenceQuery(id!, {
    skip: !id,
  });

  const [absenceApproveTrigger, absenceApproveResult] = useApproveAbsenceMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });
  const {
    isSuccess: isAbsenceApproveSuccess,
    isError: isAbsenceApproveError,
    error: errorAbsenceApprove,
    reset: resetAbsenceApprove,
  } = absenceApproveResult;

  const [absenceRejectTrigger, absenceRejectResult] = useRejectAbsenceMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });
  const {
    isSuccess: isAbsenceRejectSuccess,
    isError: isAbsenceRejectError,
    error: errorAbsenceReject,
    reset: resetAbsenceReject,
  } = absenceRejectResult;

  const rejectionAbsence = () => {
    if (id) {
      absenceRejectTrigger({ requestId: Number(id) });
    }
  };

  const approveAbsence = () => {
    if (id) {
      absenceApproveTrigger({ requestId: Number(id) });
    }
  };

  useEffect(() => {
    if (dataSingleAbsence) {
      const dataAbsence = dataObjectForFormAbsences(dataSingleAbsence);
      setDataAbsence(dataAbsence);
      const tableData = dataObjectForAbsencesTable(dataSingleAbsence);
      dataForTable.current = tableData;
    }
  }, [dataSingleAbsence]);

  useNotifications(
    isAbsenceApproveSuccess,
    isAbsenceApproveError,
    'Отправлено на согласование',
    errorAbsenceApprove?.message,
    resetAbsenceApprove,
  );

  useNotifications(
    isAbsenceRejectSuccess,
    isAbsenceRejectError,
    'Заявление успешно закрыто',
    errorAbsenceReject?.message,
    resetAbsenceReject,
  );

  useIndicators(isSuccessGetSingleAbsence, isFetchingGetSingleAbsence);

  return (
    <>
      <div className={style.wrap}>
        {isLoading ? (
          <Skeleton active />
        ) : (
          <>
            {dataAbsence && (
              <div className={style.btnsGroup}>
                <Popconfirm
                  placement="topRight"
                  title={'Вы уверены, что хотите отклонить заявление о неявке?'}
                  onConfirm={rejectionAbsence}
                  cancelText="Отмена"
                  okText="Да"
                  disabled={
                    dataSingleAbsence?.zupRequestStatus !== null ||
                    dataSingleAbsence?.status.type === 'rejectedByHR' ||
                    blockHrActions
                  }
                >
                  <Button
                    type="default"
                    disabled={
                      dataSingleAbsence?.zupRequestStatus !== null ||
                      dataSingleAbsence?.status.type === 'rejectedByHR' ||
                      blockHrActions
                    }
                  >
                    Отклонить
                  </Button>
                </Popconfirm>

                <Popconfirm
                  placement="topRight"
                  title={'Вы уверены, что хотите согласовать заявление о неявке?'}
                  onConfirm={approveAbsence}
                  cancelText="Отмена"
                  okText="Да"
                  disabled={
                    dataSingleAbsence?.zupRequestStatus !== null ||
                    dataSingleAbsence?.status.type === 'rejectedByHR' ||
                    blockHrActions
                  }
                >
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    disabled={
                      dataSingleAbsence?.zupRequestStatus !== null ||
                      dataSingleAbsence?.status.type === 'rejectedByHR' ||
                      blockHrActions
                    }
                  >
                    Согласовать
                  </Button>
                </Popconfirm>
              </div>
            )}

            <Table
              scroll={{ x: true }}
              dataSource={dataForTable.current}
              columns={columns}
              pagination={false}
              bordered
            />
            {dataAbsence && <AbsenceUserForm dataAbsence={dataAbsence} />}
          </>
        )}
      </div>

      {dataAbsence && (dataAbsence[0]?.supervisorComment || dataAbsence[0]?.hrComment) && (
        <AbsenceComment dataAbsence={dataAbsence} />
      )}
    </>
  );
};

export default AbsenceUser;
